import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { buttonBase, buttonVariants } from '@/components/landing/button-link'
import { Eyebrow } from '@/components/landing/eyebrow'
import { TicketListForm } from '@/components/landing/ticket-list-form'
import { cn } from '@/lib/utils'
import { EVENT, PASSES } from '@/constants/event'

export const TicketsSection: FC = (): ReactNode => {
    const [pass, setPass] = useState(PASSES.find((item) => item.featured)?.id ?? PASSES[0].id)
    const formRef = useRef<HTMLDivElement>(null)

    const choosePass = (id: string): void => {
        setPass(id)
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        window.setTimeout(() => formRef.current?.querySelector<HTMLInputElement>('input[name="email"]')?.focus({ preventScroll: true }), 500)
    }

    return (
        <section
            id='tickets'
            aria-labelledby='tickets-title'
            className='relative overflow-hidden bg-lime text-ink'
        >
            <div className='mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32'>
                <div className='flex flex-col justify-between gap-8 lg:flex-row lg:items-end'>
                    <div>
                        <Eyebrow>Tickets</Eyebrow>

                        <h2 id='tickets-title' className='mt-5 font-heading text-[clamp(3.2rem,8vw,7.5rem)] font-black uppercase leading-[0.86]'>
                            Watch from
                            <span className='serif-soft block font-serif font-normal normal-case italic'>the front row.</span>
                        </h2>
                    </div>

                    <p className='max-w-md text-base leading-relaxed text-ink/80 lg:pb-3'>
                        {`Passes go on sale on ${EVENT.ticketsOnSale}. Join the ticket list and you get a 48-hour head start, before general sale opens.`}
                    </p>
                </div>

                <ul className='mt-14 grid gap-4 md:mt-20 lg:grid-cols-3'>
                    {PASSES.map((item) => (
                        <li
                            key={item.id}
                            className={cn(
                                'relative flex flex-col rounded-[2rem] p-7 md:p-8',
                                item.featured ? 'bg-ink text-cream shadow-[0_40px_80px_-40px_rgba(13,20,51,0.7)]' : 'bg-cream/80 text-ink'
                            )}
                        >
                            {item.featured && (
                                <p className='absolute right-6 top-6 rounded-full bg-lime px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-ink'>
                                    Best seat
                                </p>
                            )}

                            <h3 className='font-heading text-3xl font-extrabold uppercase leading-none'>
                                {item.name}
                            </h3>

                            <p className='mt-6 flex items-baseline gap-2'>
                                <span className={cn('font-heading text-7xl font-black leading-none', item.featured && 'text-lime')}>
                                    {`$${item.price}`}
                                </span>
                                <span className={cn('text-sm', item.featured ? 'text-cream/70' : 'text-ink/65')}>
                                    {item.unit}
                                </span>
                            </p>

                            <p className={cn('mt-4 text-sm leading-relaxed', item.featured ? 'text-cream/80' : 'text-ink/75')}>
                                {item.description}
                            </p>

                            <ul className='mb-8 mt-6 flex flex-col gap-3 text-sm'>
                                {item.perks.map((perk) => (
                                    <li key={perk} className='flex items-start gap-3'>
                                        <span aria-hidden='true' className={cn('mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full', item.featured ? 'bg-lime text-ink' : 'bg-ink text-lime')}>
                                            <Check className='size-3' strokeWidth={3} />
                                        </span>
                                        {perk}
                                    </li>
                                ))}
                            </ul>

                            <button
                                type='button'
                                onClick={() => choosePass(item.id)}
                                className={cn(buttonBase, item.featured ? buttonVariants.lime : buttonVariants.ink, 'mt-auto w-full')}
                            >
                                {`Pick the ${item.name}`}
                            </button>
                        </li>
                    ))}
                </ul>

                <div ref={formRef} className='mt-6 grid scroll-mt-24 gap-8 rounded-[2rem] bg-ink p-6 text-cream md:p-10 lg:grid-cols-12 lg:gap-12'>
                    <div className='lg:col-span-5'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.2em] text-lime'>
                            The ticket list
                        </p>

                        <p className='mt-3 font-heading text-4xl font-extrabold uppercase leading-[0.92] md:text-5xl'>
                            Get in before
                            <span className='serif-soft block font-serif font-normal normal-case italic text-lime'>everyone else.</span>
                        </p>

                        <p className='mt-4 max-w-sm text-sm leading-relaxed text-cream/75'>
                            Pick the pass you want and how many of you are coming. We hold nothing yet, we just make sure you hear first.
                        </p>
                    </div>

                    <div className='lg:col-span-7'>
                        <TicketListForm pass={pass} onPassChange={setPass} />
                    </div>
                </div>
            </div>
        </section>
    )
}
